import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  Fragment,
  useCallback,
  memo,
} from "react";
import Button from "../ui-components/Button";
import getNextActiveStep from "../utils/getNextActiveStep";
import isNextStepButtonVisible from "../utils/isNextStepButtonVisible";
import ConfiguratorSurveyBlockHeader from "./ConfiguratorSurveyBlockHeader";
import { FormattedMessage, useIntl } from "react-intl";
import { NotificationsContext } from "../ui-components/Notifications";
import Tabs from "../ui-components/Tabs";
import useSetResponsesPreviousValues from "../ui-components/pages/Users/hooks/useSetResponsesPreviousValues";
import checkIfBlockIsVisible from "../utils/checkIfBlockIsVisible";
import ConfiguratorSurveyRecursiveQuestion from "./ConfiguratorSurveyRecursiveQuestion";
import ConfiguratorSurveyBlockRender from "./ConfiguratorSurveyBlockRender";
import ConfiguratorSurveySummaryStep from "./ConfiguratorSurveySummaryStep";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import updateResponsesObject from "../utils/updateResponsesObject";
import getSelectQuestionsWithOneOption from "../utils/getSelectQuestionsWithOneOption";
import ConfiguratorSurveyRealTimeSummary from "./ConfiguratorSurveyRealTimeSummary";
import ConfiguratorContext from "./ConfiguratorContext";
import CloseIcon from "./CloseIcon";
import { Link, useHistory } from "react-router-dom";
import AuthenticationService from "../services/AuthenticationService";
import Alert from "@ui-components/Alert";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import CountryContext from "../CountryContext";
import { getSurveyResponsesToRemove } from "../utils/getSurveyResponsesToRemove";
import { storeConfigurationKpi } from "../utils/storeConfigurationKpi";
import useGetOptionTitle from "../hooks/useGetOptionTitle";


const ConfiguratorSurvey = ({
  selectedRoleType,
  saveResponses,
  visibleSteps,
  returnPath,
  updateSteps,
  updateServicesSurveys,
  associatedServices = [],
}) => {
  const myRef = useRef(null);
  const local_user = AuthenticationService.getUserData();
  const role = selectedRoleType ? selectedRoleType : local_user["role_type"];
  const user = useMemo(() => ({
    ...local_user,
    role_type: role,
  }), [local_user, role]);
  const context = useContext(ConfiguratorContext);
  const history = useHistory();
  const [activeStep, setActiveStep] = useState(() =>
    getNextActiveStep(user, null, context)
  );
  const getOptionTitle = useGetOptionTitle();

  const notHiddenVisibileSteps = visibleSteps.filter(
    (i) => !i.hidden_question.includes(`role_type_${role}`)
  );

  const locked = context?.survey?.not_editable === true || context.worksite_freezed;

  const intl = useIntl();
  const { push } = useContext(NotificationsContext);
  const [submitting, setSubmitting] = useState(false);
  const [nextStepVisible, setNextStepVisible] = useState(false);
  const [confWidth, setConfWidth] = useState("");
  const apiRequest = useCountryAxiosInstance();

  const { country } = useContext(CountryContext);

  useEffect(() => {
    // only for admin preview
    if (selectedRoleType) {
      setActiveStep(
        getNextActiveStep(user, null, { ...context, responses: {} })
      );
    }
    // eslint-disable-next-line
  }, [selectedRoleType, setActiveStep]);

  // used to clear responses not saved
  const setResponsesPreviousValues = useSetResponsesPreviousValues(activeStep);

  useEffect(() => {
    if (!activeStep.complete) {
      const isVisible = isNextStepButtonVisible(activeStep, context);
      setNextStepVisible(isVisible);
    }
  }, [context, setNextStepVisible, activeStep]);

  const onChange = useCallback(
    (value, option, block, recursive_question_value) => {
      let newResponse = {};

      let questionWithOneOptionPreChange = {};
      if (!recursive_question_value) {
        // retrieve question with only one option only if is not a recursive question
        questionWithOneOptionPreChange = getSelectQuestionsWithOneOption(
          activeStep.questions,
          value,
          block,
          !block,
          context,
          user
        );
      }

      if (block) {
        newResponse = updateResponsesObject(
          context.responses,
          block,
          value,
          option,
          recursive_question_value,
          context
        );
      }

      let questionWithOneOption = {};
      if (!recursive_question_value) {
        // retrieve question with only one option only if is not a recursive question
        questionWithOneOption = getSelectQuestionsWithOneOption(
          activeStep.questions,
          value,
          block,
          !block,
          {
            ...context,
            ...(Object.keys(newResponse ?? {}).length
              ? { responses: newResponse }
              : {}),
          },
          user
        );
      }
      // when when choosing an answer that removes old questions with one options
      // we need to remove the responses that were activated by the old question
      /*
      getSurveyResponsesToRemove already does that!!!! (recursively)
       */
      // Object.keys(questionWithOneOptionPreChange).forEach((key) => {
      //   delete newResponse[key];
      //   delete context.responses[key];
      // });

      // it might be possible that the user already responded to the current question; in addition to that, it is also
      // possible that the user already responded to some other questions that were activated by the old response.
      // in this scenario, we need to remove the responses that were activated by the old response (NOTE: we are not
      // going to remove all the dependant responses, but only those that are associated to a question that is not
      // active anymore)
      const responsesToRemove = getSurveyResponsesToRemove(block?.id, {
        ...context,
        responses: {
          ...context.responses,
          ...newResponse,
        },
      });

      context.setResponses((prev) => {
        const updated = {
          ...prev,
          ...newResponse,
          ...questionWithOneOption,
        };
        return _.omit(updated, responsesToRemove);
      });
    },
    [context, activeStep, user]
  );

  useEffect(() => {
    if (!activeStep.complete) {
      setConfWidth("lg:w-2/3");
      onChange();
    } else {
      setConfWidth("");
    }
    // eslint-disable-next-line
  }, [activeStep]);

  const {
    title,
    description,
    image_filename,
    blocks,
    complete,
    hidden_question,
  } = activeStep;
  const isBlockHiddenByRole =
    hidden_question?.includes(`role_type_${role}`) ?? false;

  const renderSourceSurveys = (sources) => {
    return (
      <div className="my-3">
        <Alert
          type="success"
          text={
            <div className="flex flex-col gap-2 mt-0.5">
              <p className="text-xs">
                <FormattedMessage id="quotation_generated_from" />
              </p>
              <ul className="list-disc list-inside">
                {sources.map((s, idx) => {
                  const { worksite_id, service_id, id, name } = s;
                  return (
                    <li key={idx}>
                      <span className="font-semibold">
                        {name? name: <FormattedMessage id="unnamed_configuration" />}
                      </span>
                      <Link
                        to={`/${country.code}/worksites/${worksite_id}/service/${service_id}/survey/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ArrowTopRightOnSquareIcon className="inline w-4 ml-2 -mt-1 text-green-700 stroke-2" />
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          }
          slim
        />
      </div>
    );
  };
  
  return (
    <div className="w-full flex flex-col lg:flex-row" ref={myRef}>
      <div className={`w-full flex ${confWidth} flex-col mx-auto px-6 pb-10`}>
        <CloseIcon
          onClick={() => {
            history.push(returnPath);
          }}
        >
          {!!context.sourceSurveys?.length &&
            renderSourceSurveys(context.sourceSurveys)}
        </CloseIcon>
        {notHiddenVisibileSteps?.length ? (
          <div className="w-full pb-12">
            <Tabs
              setActiveTab={(index) => {
                setResponsesPreviousValues();
                if (index === notHiddenVisibileSteps.length) {
                  setActiveStep({ complete: true, title: "summary" });
                  return;
                }
                setActiveStep(notHiddenVisibileSteps[index]);
              }}
              active={
                complete
                  ? notHiddenVisibileSteps.length
                  : notHiddenVisibileSteps.reduce((acc, step, index) => {
                      return acc || acc === 0
                        ? acc
                        : (activeStep.id === step.id)
                        ? (activeStep.service_block_id === step.service_block_id) ? index: acc
                        : acc;
                    }, null)
              }
              tabs={[
                ...notHiddenVisibileSteps.map((step, index) => ({
                  name: step.title
                    ? intl.formatMessage({
                        id: step.title,
                      })
                    : step.name
                    ? step.name
                    : `Step ${index}`,
                })),
                ...(context?.survey?.complete
                  ? [
                      {
                        name: intl.formatMessage({
                          id: "summary",
                          defaultMessage: "Summary",
                        }),
                        onClick: async () => {
                          // In caso di modifica ad un tab precedente.
                          try {
                            await storeConfigurationKpi(
                              context,
                              getOptionTitle,
                              apiRequest
                            );
                          } catch (error) {
                            console.log(error);
                            push({
                              title: intl.formatMessage({ id: "server_error" }),
                              type: "error",
                            });
                          }
                        },
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        ) : (
          <div style={{ height: "90px" }} />
        )}
        <div className="w-full flex flex-col md:flex-row">
          <div
            className={`w-full flex flex-col md:pr-6 ${
              complete ? "container mx-auto" : "w-full"
            }`}
          >
            {complete ? (
              <ConfiguratorSurveySummaryStep
                history={history}
                returnPath={returnPath}
                role={role}
                associatedServices={associatedServices}
              />
            ) : (
              <Fragment>
                {locked && (
                  <div className="w-full mb-8">
                    <Alert
                      type="warning"
                      title={
                      context?.survey?.not_editable ?
                        <FormattedMessage id="survey_not_editable_helper_title" />:
                        <FormattedMessage id="worksite_freezed_helper_title" />
                      }
                      text={
                        context?.survey?.not_editable ?
                        <FormattedMessage id="survey_not_editable_helper_description" />:
                        <FormattedMessage id="worksite_freezed_helper_description" />
                      }
                    />
                  </div>
                )}
                <div className="relative">
                  {locked && (
                    <div className="absolute inset-0 bg-white opacity-60 cursor-not-allowed z-50" />
                  )}
                  <div className="mb-8">
                    <ConfiguratorSurveyBlockHeader
                      title={title}
                      description={description}
                      image_filename={image_filename}
                      hidden={isBlockHiddenByRole ? "hidden" : ""}
                    />
                  </div>
                  {blocks?.length && !isBlockHiddenByRole ? (
                    <div className="w-full flex flex-col">
                      {blocks
                        .filter((block) =>
                          checkIfBlockIsVisible(block, context)
                        )
                        .map((block) => {
                          if (block.type === "recursive_question") {
                            return (
                              <ConfiguratorSurveyRecursiveQuestion
                                key={block.id}
                                list={
                                  context.responses?.[block.id]
                                    ?.recursive_question_value || []
                                }
                                onChange={(recursive_question_value) =>
                                  onChange(
                                    undefined,
                                    undefined,
                                    block,
                                    recursive_question_value
                                  )
                                }
                                role={role}
                                hidden_question={block.hidden_question}
                                table_mode={block.metadata_?.table_mode}
                                {...block}
                              />
                            );
                          }
                          return (
                            <ConfiguratorSurveyBlockRender
                              key={block.id}
                              block={block}
                              onChange={(value, option) =>
                                onChange(value, option, block)
                              }
                              role={role}
                              saveResponses={async () => {
                                if (locked || submitting) return;

                                try {
                                  if (saveResponses) {
                                    setSubmitting(true);

                                    await saveResponses([
                                      ...activeStep.questions.map((q) => q.id),
                                      ...activeStep.recursiveQuestions.map(
                                        (q) => q.id
                                      ),
                                    ]);
                                    setSubmitting(false);
                                  }
                                  return true;
                                } catch (error) {
                                  console.log(error);

                                  setSubmitting(false);
                                  push({
                                    title: intl.formatMessage({
                                      id: "server_error",
                                    }),
                                    type: "error",
                                  });
                                }
                              }}
                              hideOutput
                            />
                          );
                        })}
                    </div>
                  ) : null}
                  {nextStepVisible ? (
                    <div className="w-full flex justify-end  pb-8">
                      <Button
                        styleType="empty"
                        submitting={submitting}
                        onClick={async () => {
                          if (locked || submitting) return;

                          try {
                            if (saveResponses) {
                              setSubmitting(true);
                              /*
                              HUBSPOT TICKET # 42912186232
                              we need to remove autogenerated responses of questions inside a recursive question
                              because they are not inside the recursive question!!! they are iat base level!!
                              */
                              if (Object.keys(context.autogeneratedResponsesToRemove ?? {}).length) {
                                const idsToRemove = Object.keys(context.autogeneratedResponsesToRemove).map((key) => context.autogeneratedResponsesToRemove[key].id);
                                await apiRequest.post("service_survey_responses/bulk_delete", {
                                  ids: idsToRemove
                                });
                                context.setAutogeneratedResponsesToRemove({});
                              }
                              await saveResponses([
                                ...activeStep.questions.map((q) => q.id),
                                ...activeStep.recursiveQuestions.map(
                                  (q) => q.id
                                ),
                              ]);
                              setSubmitting(false);
                            } else {
                              updateSteps();
                            }

                            const currentStep = context.data.reduce(
                              (acc, step, index) =>
                                step.id === activeStep.id ? index : acc,
                              0
                            );

                            const nextStep = getNextActiveStep(
                              user,
                              currentStep,
                              context,
                              true
                            );

                            if (nextStep.complete && context.survey) {
                              await apiRequest.put(
                                `/service_surveys/${context.survey.id}`,
                                {
                                  ...context.survey,
                                  complete: true,
                                  modified_at: new Date(),
                                  id_modifier: user.id,
                                  first_render:
                                    context.survey.first_render === null,
                                }
                              );

                              await updateServicesSurveys();
                              await storeConfigurationKpi(
                                context,
                                getOptionTitle,
                                apiRequest
                              );
                            }

                            setActiveStep(nextStep);
                          } catch (error) {
                            console.log(error);

                            setSubmitting(false);
                            push({
                              title: intl.formatMessage({ id: "server_error" }),
                              type: "error",
                            });
                          } finally {
                            if (myRef.current) {
                              myRef.current.scrollIntoView();
                            }
                          }
                        }}
                      >
                        <FormattedMessage id="next" defaultMessage="NEXT" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              </Fragment>
            )}
          </div>
        </div>
      </div>
      {!complete ? (
        <ConfiguratorSurveyRealTimeSummary
          activeStep={activeStep}
          role={role}
        />
      ) : null}
    </div>
  );
};

export default memo(ConfiguratorSurvey);
